import { matchPath } from "react-router";

// Mirrors the api's own PAGE_ROUTE_FEATURES map (api app/shared/constants/featureUsage.ts,
// 37-feature-usage-tracking.md "Page views") -- one entry per route the api attributes to a feature.
// Add new templates to BOTH files. Static templates are listed before parameterized ones on purpose:
// matchPath tries patterns in array order and returns the first hit, so a literal segment (e.g.
// "manage") must be tried before the generic `/:id` template that would otherwise swallow it as the
// param value.
const RECOGNITIONS_TEMPLATE = "/recognitions";

export const PAGE_ROUTE_TEMPLATES: readonly string[] = [
  // static
  "/accountability-chart",
  "/admin/audit-log",
  "/admin/evaluations",
  "/admin/procedure-index",
  "/admin/settings",
  "/goals",
  "/procedure-assistant",
  "/procedures",
  "/procedures/manage",
  "/procedures/onboarding",
  "/procedures/processes",
  "/projects",
  RECOGNITIONS_TEMPLATE,
  "/recognitions/all",
  "/recognitions/badges",
  "/surveys",
  "/users",
  // parameterized -- every param name is written as `:id` since matchPath only cares about
  // segment shape, and `:id` is exactly what this module returns to the caller.
  "/admin/audit-log/:id",
  "/admin/evaluations/:id",
  "/admin/settings/:id",
  "/procedure-assistant/:id",
  "/procedure-assistant/:id/sources/:id",
  "/procedures/:id",
  "/procedures/processes/:id",
  "/surveys/:id",
  "/users/:id",
];

// The only query param the api's PAGE_ROUTE_FEATURES keys on -- the six tab slugs
// RecognitionsTabBarContainer switches between via `?section=`.
const RECOGNITION_SECTIONS: readonly string[] = ["admin", "leaderboard", "overview", "redeem", "redemptions", "review"];

// A path matching no known template is sent as this fixed string, never the raw pathname -- nothing
// unknown (which may carry an id, a slug, or worse) ever leaves the browser. The api records it under
// its own `unmapped` feature key.
export const UNMAPPED_PAGE_VIEW_TEMPLATE = "/unmapped";

/**
 * Turns a location's pathname + query into the route template the api's page-view endpoint expects.
 *
 * @param pathname The location's pathname.
 * @param search The location's query string, including the leading `?` when present.
 * @returns The matching template, or {@link UNMAPPED_PAGE_VIEW_TEMPLATE}.
 */
export const getPageViewTemplate = (pathname: string, search: string): string => {
  const template = PAGE_ROUTE_TEMPLATES.find((candidate) => matchPath(candidate, pathname) !== null);

  if (template === undefined) {
    return UNMAPPED_PAGE_VIEW_TEMPLATE;
  }

  if (template === RECOGNITIONS_TEMPLATE) {
    const section = new URLSearchParams(search).get("section");
    return section && RECOGNITION_SECTIONS.includes(section)
      ? `${RECOGNITIONS_TEMPLATE}?section=${section}`
      : RECOGNITIONS_TEMPLATE;
  }

  return template;
};
